import { useEffect, useRef, useCallback, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { currentClassIdAtom, currentClassroomAtom, currentMessagesAtom, currentSessionIdAtom, historyResetSessionIdsAtom, isSessionsLoadingAtom, classroomsAtom } from '../state/store'
import { useScrollToBottom } from '../hooks/useScrollToBottom'
import { useLoadMoreMessages } from '../hooks/useLoadMoreMessages'
import { ChatMessageBubble } from './ChatMessageBubble'
import { UserMessageBubble } from './UserMessageBubble'
import { ScrollToBottom } from './ScrollToBottom'
import { chatInputActionsRef } from './ChatInput'
import { StudentHome } from './StudentHome'
import { ClassHome } from './ClassHome'
import { GeneralChatHome } from './GeneralChatHome'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { joinKnownClassByCode } from '../api/backend'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'
import { Input } from './ui/input'
import { Button } from './ui/button'

function MessageSkeleton() {
  return (
    <div className="max-w-[840px] mx-auto px-5 py-4 animate-pulse">
      {/* User message skeleton */}
      <div className="flex justify-end mb-4">
        <div className="h-10 w-48 rounded-base bg-foreground/10 border-2 border-nb-border/30" />
      </div>
      {/* Assistant message skeleton */}
      <div className="flex gap-3 mb-4">
        <div className="shrink-0 w-8 h-8 rounded-base bg-foreground/10 border-2 border-nb-border/30" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 rounded-base bg-foreground/10" />
          <div className="h-4 w-1/2 rounded-base bg-foreground/10" />
          <div className="h-4 w-2/3 rounded-base bg-foreground/10" />
        </div>
      </div>
      {/* Another pair */}
      <div className="flex justify-end mb-4">
        <div className="h-10 w-32 rounded-base bg-foreground/10 border-2 border-nb-border/30" />
      </div>
      <div className="flex gap-3">
        <div className="shrink-0 w-8 h-8 rounded-base bg-foreground/10 border-2 border-nb-border/30" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-2/3 rounded-base bg-foreground/10" />
          <div className="h-4 w-1/2 rounded-base bg-foreground/10" />
        </div>
      </div>
    </div>
  )
}

export function MessageList() {
  const navigate = useNavigate()
  const messages = useAtomValue(currentMessagesAtom)
  const sessionId = useAtomValue(currentSessionIdAtom)
  const currentClassId = useAtomValue(currentClassIdAtom)
  const currentClassroom = useAtomValue(currentClassroomAtom)
  const setClassrooms = useSetAtom(classroomsAtom)
  const historyResetSessionIds = useAtomValue(historyResetSessionIdsAtom)
  const isSessionsLoading = useAtomValue(isSessionsLoadingAtom)
  const { getAccessToken } = useAuth()
  const { memberRole } = useWorkspace()
  const { containerRef, isAtBottom, scrollToBottom } = useScrollToBottom()
  const { loadMore, isLoadingMore, hasMore, loadingRef } = useLoadMoreMessages()
  const [classCodeInput, setClassCodeInput] = useState('')
  const [isJoiningClass, setIsJoiningClass] = useState(false)
  const prevSessionIdRef = useRef(sessionId)
  const prevMsgCountRef = useRef(0)
  const lastScrollTopRef = useRef(0)
  const prevLastMessageKeyRef = useRef<string | null>(null)

  // Scroll to bottom when entering a chat: either on session switch or
  // when history messages arrive (messages go from 0 → N).
  useEffect(() => {
    const sessionChanged = prevSessionIdRef.current !== sessionId
    const wasEmpty = prevMsgCountRef.current === 0
    prevSessionIdRef.current = sessionId
    prevMsgCountRef.current = messages.length
    lastScrollTopRef.current = 0

    if (messages.length > 0 && (sessionChanged || wasEmpty)) {
      requestAnimationFrame(() => {
        const el = containerRef.current
        if (el) {
          el.scrollTop = el.scrollHeight
          lastScrollTopRef.current = el.scrollTop
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, messages.length])

  // Preserve scroll position after older messages are prepended.
  // Snapshot scrollHeight before loading; restore after DOM updates.
  const scrollSnapshotRef = useRef(0)

  const handleLoadMore = useCallback(() => {
    const el = containerRef.current
    if (el) {
      scrollSnapshotRef.current = el.scrollHeight
    }
    loadMore()
  }, [containerRef, loadMore])

  // When messages change and we have a saved snapshot, restore scroll position
  useEffect(() => {
    if (scrollSnapshotRef.current > 0 && !isLoadingMore) {
      requestAnimationFrame(() => {
        const el = containerRef.current
        if (el) {
          el.scrollTop = el.scrollHeight - scrollSnapshotRef.current
        }
        scrollSnapshotRef.current = 0
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isLoadingMore])

  // Keep the latest message visible when a new question is asked or
  // while the assistant is streaming near the bottom.
  useEffect(() => {
    const el = containerRef.current
    const lastMessage = messages[messages.length - 1]
    if (!el || !lastMessage) {
      prevLastMessageKeyRef.current = null
      return
    }

    const lastMessageKey = `${lastMessage.id}:${lastMessage.content}:${lastMessage.isStreaming ? 'streaming' : 'done'}`
    const messageChanged = prevLastMessageKeyRef.current !== lastMessageKey
    prevLastMessageKeyRef.current = lastMessageKey

    if (!messageChanged || scrollSnapshotRef.current > 0) return

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const isNearBottom = distanceFromBottom < 160
    const shouldStickToBottom =
      lastMessage.role === 'user'
      || (lastMessage.role === 'assistant' && (lastMessage.isStreaming || isNearBottom))

    if (shouldStickToBottom) {
      requestAnimationFrame(() => {
        const nextEl = containerRef.current
        if (nextEl) {
          nextEl.scrollTop = nextEl.scrollHeight
          lastScrollTopRef.current = nextEl.scrollTop
        }
      })
    }
  }, [containerRef, messages])

  // Load older messages when scrolling near the top.
  // Uses loadingRef (synchronous) instead of isLoadingMore (async state)
  // to prevent multiple triggers before React re-renders.
  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el || !hasMore || loadingRef.current) return
    if (el.scrollHeight <= el.clientHeight + 24) return

    const currentScrollTop = el.scrollTop
    const isScrollingUp = currentScrollTop < lastScrollTopRef.current

    lastScrollTopRef.current = currentScrollTop

    if (isScrollingUp && currentScrollTop <= 80) {
      handleLoadMore()
    }
  }, [containerRef, hasMore, loadingRef, handleLoadMore])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [containerRef, handleScroll])

  useEffect(() => {
    if (!currentClassId) return
    if (memberRole === 'TEACHER' || memberRole === 'ADMIN') return
    if (currentClassroom?.isJoined) return
    setClassCodeInput('')
  }, [currentClassId, currentClassroom?.isJoined, memberRole])

  const handleJoinCurrentClassroom = useCallback(async () => {
    const token = getAccessToken()
    if (!token || !currentClassId || !classCodeInput.trim()) return

    setIsJoiningClass(true)
    try {
      const result = await joinKnownClassByCode(token, currentClassId, {
        classCode: classCodeInput.trim().toUpperCase(),
      })
      setClassrooms((prev) =>
        prev.some((item) => item.id === result.classroom.id)
          ? prev.map((item) =>
              item.id === result.classroom.id
                ? { ...item, ...result.classroom, isJoined: true }
                : item,
            )
          : [{ ...result.classroom, isJoined: true }, ...prev],
      )
      setClassCodeInput('')
      navigate(`/classes/${result.classroom.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to join class'
      if (/invalid class code/i.test(message)) {
        toast.error('That class code does not match. Check the code and try again.')
      } else if (/not available to join right now/i.test(message) || /not accepting new students/i.test(message)) {
        toast.error('This class is not available to join right now.')
      } else if (/classroom not found/i.test(message)) {
        toast.error('This class link is no longer available.')
        navigate('/')
      } else {
        toast.error(message)
      }
    } finally {
      setIsJoiningClass(false)
    }
  }, [classCodeInput, currentClassId, getAccessToken, navigate, setClassrooms])

  // Show skeleton while sessions/history are being loaded
  if (isSessionsLoading && messages.length === 0) {
    if (!sessionId && !currentClassId) {
      return <StudentHome loading />
    }
    return (
      <div className="flex-1 overflow-y-auto">
        <MessageSkeleton />
      </div>
    )
  }

  if (currentClassId && memberRole !== 'TEACHER' && memberRole !== 'ADMIN' && !currentClassroom?.isJoined) {
    return (
      <div className="flex h-full items-center justify-center px-5 py-6">
        <Dialog open onOpenChange={(open) => {
          if (!open) {
            setClassCodeInput('')
            navigate('/')
          }
        }}>
          <DialogContent className="max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Enter Class Code</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3">
              <p className="text-sm text-foreground/60">
                Enter the class code to join this class.
              </p>
              <Input
                value={classCodeInput}
                onChange={(event) => setClassCodeInput(event.target.value.toUpperCase())}
                placeholder="ARTS-01"
              />
            </div>
            <DialogFooter>
              <Button variant="neutral" size="sm" onClick={() => {
                setClassCodeInput('')
                navigate('/')
              }}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void handleJoinCurrentClassroom()} disabled={isJoiningClass || !classCodeInput.trim()}>
                {isJoiningClass ? 'Joining...' : 'Join Class'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  if (messages.length === 0) {
    if (sessionId && historyResetSessionIds.has(sessionId)) {
      return (
        <div className="flex-1 overflow-y-auto">
          <MessageSkeleton />
        </div>
      )
    }

    if (sessionId && currentClassId) {
      return (
        <div className="flex-1 overflow-y-auto">
          <MessageSkeleton />
        </div>
      )
    }

    if (sessionId && !currentClassId) {
      return <GeneralChatHome />
    }

    if (!currentClassId) {
      return <StudentHome />
    }

    if (currentClassroom) {
      return (
        <ClassHome
          classroom={currentClassroom}
          onStarterPromptClick={(text) => chatInputActionsRef.current.onSend?.(text, [])}
        />
      )
    }
    return <StudentHome />
  }

  // Find the last assistant message index
  let lastAssistantIndex = -1
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') {
      lastAssistantIndex = i
      break
    }
  }

  return (
    <div className="relative flex-1 min-h-0">
      <div ref={containerRef} className="h-full overflow-y-auto">
        <div className="max-w-[840px] mx-auto px-5 py-4">
          {isLoadingMore && (
            <div className="flex justify-center py-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground/60" />
            </div>
          )}
          {messages.map((msg, index) =>
            msg.role === 'assistant' ? (
              <ChatMessageBubble
                key={msg.id}
                message={msg}
                isLast={index === lastAssistantIndex}
              />
            ) : (
              <UserMessageBubble
                key={msg.id}
                message={msg}
              />
            ),
          )}
        </div>
      </div>
      <ScrollToBottom visible={!isAtBottom} onClick={scrollToBottom} />
    </div>
  )
}
